<!-- WalletDetailModal.vue -->
<template>
  <div class="modal fade" id="walletDetailModal" tabindex="-1" role="dialog">
    <div class="modal-dialog modal-xl modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">
            <i class="mdi mdi-wallet text-primary me-2"></i>
            Wallet Details - {{ wallet.walletId }}
          </h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body">
          <!-- Wallet Information -->
          <div class="row mb-4">
            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-primary text-white">
                  <h6 class="mb-0">Account Information</h6>
                </div>
                <div class="card-body">
                  <table class="table table-borderless mb-0">
                    <tr>
                      <td class="fw-semibold">Wallet ID:</td>
                      <td>{{ wallet.walletId }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Account Number:</td>
                      <td>{{ wallet.accountNumber }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Account Holder:</td>
                      <td>{{ wallet.holderName }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Phone Number:</td>
                      <td>{{ wallet.phoneNumber }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Email:</td>
                      <td>{{ wallet.email }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Wallet Type:</td>
                      <td>
                        <span class="badge" :class="getWalletTypeBadge(wallet.type)">
                          {{ wallet.type }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Status:</td>
                      <td>
                        <span class="badge" :class="getStatusBadge(wallet.status)">
                          {{ wallet.status }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">KYC Status:</td>
                      <td>
                        <span class="badge" :class="wallet.kycStatus === 'VERIFIED' ? 'bg-success' : 'bg-warning'">
                          {{ wallet.kycStatus }}
                        </span>
                      </td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Tier:</td>
                      <td>{{ wallet.tier }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Date Created:</td>
                      <td>{{ wallet.dateCreated }}</td>
                    </tr>
                  </table>
                </div>
              </div>
            </div>

            <div class="col-md-6">
              <div class="card border">
                <div class="card-header bg-success text-white">
                  <h6 class="mb-0">Balance Information</h6>
                </div>
                <div class="card-body">
                  <div class="text-center mb-3">
                    <h2 class="text-success">{{ formatPrice(wallet.balance) }}</h2>
                    <p class="text-muted mb-0">Total Balance</p>
                  </div>

                  <table class="table table-borderless mb-0">
                    <tr>
                      <td class="fw-semibold">Available Balance:</td>
                      <td class="text-end">{{ formatPrice(wallet.availableBalance) }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Frozen Amount:</td>
                      <td class="text-end">{{ formatPrice(wallet.balance - wallet.availableBalance) }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Daily Limit:</td>
                      <td class="text-end">{{ formatPrice(getDailyLimit(wallet.tier)) }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Monthly Limit:</td>
                      <td class="text-end">{{ formatPrice(getMonthlyLimit(wallet.tier)) }}</td>
                    </tr>
                    <tr>
                      <td class="fw-semibold">Today's Usage:</td>
                      <td class="text-end">{{ formatPrice(getTodayUsage()) }}</td>
                    </tr>
                  </table>

                  <div class="mt-3">
                    <div class="progress mb-2">
                      <div class="progress-bar" role="progressbar"
                           :style="{width: getDailyUsagePercent() + '%'}">
                        {{ getDailyUsagePercent() }}%
                      </div>
                    </div>
                    <small class="text-muted">Daily Limit Usage</small>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Tabs for Transactions and Cards -->
          <ul class="nav nav-tabs" id="walletTabs" role="tablist">
            <li class="nav-item" role="presentation">
              <button class="nav-link active" id="transactions-tab" data-bs-toggle="tab"
                      data-bs-target="#transactions" type="button" role="tab">
                <i class="mdi mdi-swap-horizontal me-2"></i>
                Recent Transactions
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link" id="cards-tab" data-bs-toggle="tab"
                      data-bs-target="#cards" type="button" role="tab">
                <i class="mdi mdi-credit-card me-2"></i>
                Associated Cards
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link" id="limits-tab" data-bs-toggle="tab"
                      data-bs-target="#limits" type="button" role="tab">
                <i class="mdi mdi-shield-check me-2"></i>
                Limits & Security
              </button>
            </li>
            <li class="nav-item" role="presentation">
              <button class="nav-link" id="history-tab" data-bs-toggle="tab"
                      data-bs-target="#history" type="button" role="tab">
                <i class="mdi mdi-history me-2"></i>
                Account History
              </button>
            </li>
          </ul>

          <div class="tab-content" id="walletTabsContent">
            <!-- Transactions Tab -->
            <div class="tab-pane fade show active" id="transactions" role="tabpanel">
              <div class="table-responsive mt-3">
                <table class="table table-hover">
                  <thead class="table-light">
                  <tr>
                    <th>Date & Time</th>
                    <th>Description</th>
                    <th>Type</th>
                    <th class="text-end">Amount</th>
                    <th class="text-end">Balance</th>
                    <th>Reference</th>
                    <th>Channel</th>
                    <th>Actions</th>
                  </tr>
                  </thead>
                  <tbody>
                  <tr v-for="transaction in transactions" :key="transaction.id">
                    <td>
                      <div>
                        {{ formatDateTime(transaction.date) }}
                      </div>
                    </td>
                    <td>{{ transaction.description }}</td>
                    <td>
                        <span class="badge" :class="transaction.type === 'CREDIT' ? 'bg-success' : 'bg-danger'">
                          {{ transaction.type }}
                        </span>
                    </td>
                    <td class="text-end">
                        <span :class="transaction.amount > 0 ? 'text-success' : 'text-danger'">
                          {{ transaction.amount > 0 ? '+' : '' }}{{ formatPrice(Math.abs(transaction.amount)) }}
                        </span>
                    </td>
                    <td class="text-end">{{ formatPrice(transaction.balance) }}</td>
                    <td>
                      <small class="text-muted">{{ transaction.reference }}</small>
                    </td>
                    <td>
                      <span class="badge bg-secondary">{{ transaction.channel }}</span>
                    </td>
                    <td>
                      <div class="dropdown">
                        <button class="btn btn-sm btn-outline-secondary dropdown-toggle" data-bs-toggle="dropdown">
                          <i class="bx bx-dots-vertical"></i>
                        </button>
                        <ul class="dropdown-menu">
                          <li><a class="dropdown-item" href="#"><i class="mdi mdi-eye me-2"></i>View Details</a></li>
                          <li><a class="dropdown-item" href="#"><i class="mdi mdi-printer me-2"></i>Print Receipt</a></li>
                          <li><a class="dropdown-item" href="#"><i class="mdi mdi-email me-2"></i>Email Receipt</a></li>
                          <li v-if="transaction.type === 'DEBIT'"><hr class="dropdown-divider"></li>
                          <li v-if="transaction.type === 'DEBIT'"><a class="dropdown-item text-danger" href="#"><i class="mdi mdi-undo me-2"></i>Reverse Transaction</a></li>
                        </ul>
                      </div>
                    </td>
                  </tr>
                  </tbody>
                </table>

                <div v-if="transactions.length === 0" class="text-center p-4">
                  <i class="mdi mdi-information-outline font-size-24 text-muted"></i>
                  <p class="text-muted mt-2">No transactions found</p>
                </div>
              </div>
            </div>

            <!-- Cards Tab -->
            <div class="tab-pane fade" id="cards" role="tabpanel">
              <div class="row mt-3">
                <div v-for="card in cards" :key="card.id" class="col-md-6 mb-3">
                  <div class="card border card-hover" :class="card.status === 'ACTIVE' ? 'border-success' : 'border-danger'">
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-start">
                        <div>
                          <h6 class="card-title">
                            <i class="mdi mdi-credit-card me-2"></i>
                            {{ card.brand }} {{ card.cardType }}
                          </h6>
                          <p class="card-text">
                            <strong>{{ card.cardNumber }}</strong><br>
                            <small class="text-muted">Expires: {{ card.expiryDate }}</small><br>
                            <small class="text-muted">Issued: {{ card.issuedDate }}</small>
                          </p>
                        </div>
                        <span class="badge" :class="card.status === 'ACTIVE' ? 'bg-success' : 'bg-danger'">
                          {{ card.status }}
                        </span>
                      </div>

                      <hr>

                      <div class="row text-center">
                        <div class="col-6">
                          <small class="text-muted d-block">Daily Limit</small>
                          <strong>{{ formatPrice(card.dailyLimit) }}</strong>
                        </div>
                        <div class="col-6">
                          <small class="text-muted d-block">Monthly Limit</small>
                          <strong>{{ formatPrice(card.monthlyLimit) }}</strong>
                        </div>
                      </div>

                      <div class="mt-3">
                        <div class="progress mb-2" style="height: 6px;">
                          <div class="progress-bar bg-info" role="progressbar" style="width: 35%"></div>
                        </div>
                        <small class="text-muted">Monthly Usage: 35%</small>
                      </div>

                      <div class="mt-3 d-flex justify-content-between">
                        <button v-if="card.status === 'ACTIVE'"
                                @click="$emit('block-card', card.id)"
                                class="btn btn-sm btn-danger">
                          <i class="mdi mdi-block-helper"></i> Block
                        </button>
                        <button v-else
                                @click="$emit('unblock-card', card.id)"
                                class="btn btn-sm btn-success">
                          <i class="mdi mdi-check-circle"></i> Unblock
                        </button>

                        <button class="btn btn-sm btn-info">
                          <i class="mdi mdi-cog"></i> Settings
                        </button>

                        <button class="btn btn-sm btn-warning">
                          <i class="mdi mdi-refresh"></i> Replace
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div v-if="cards.length === 0" class="col-12">
                  <div class="text-center p-4">
                    <i class="mdi mdi-credit-card-off font-size-48 text-muted"></i>
                    <h5 class="mt-3">No cards associated with this wallet</h5>
                    <p class="text-muted">Issue a new card to enable card-based transactions</p>
                    <button class="btn btn-primary">
                      <i class="mdi mdi-plus"></i> Issue New Card
                    </button>
                  </div>
                </div>
              </div>
            </div>

            <!-- Limits & Security Tab -->
            <div class="tab-pane fade" id="limits" role="tabpanel">
              <div class="row mt-3">
                <div class="col-md-6">
                  <div class="card border">
                    <div class="card-header">
                      <h6 class="mb-0">
                        <i class="mdi mdi-cash-multiple text-primary me-2"></i>
                        Transaction Limits
                      </h6>
                    </div>
                    <div class="card-body">
                      <table class="table table-borderless">
                        <tr>
                          <td>Single Transaction:</td>
                          <td class="text-end fw-semibold">{{ formatPrice(getSingleTransactionLimit(wallet.tier)) }}</td>
                        </tr>
                        <tr>
                          <td>Daily Limit:</td>
                          <td class="text-end fw-semibold">{{ formatPrice(getDailyLimit(wallet.tier)) }}</td>
                        </tr>
                        <tr>
                          <td>Monthly Limit:</td>
                          <td class="text-end fw-semibold">{{ formatPrice(getMonthlyLimit(wallet.tier)) }}</td>
                        </tr>
                        <tr>
                          <td>Annual Limit:</td>
                          <td class="text-end fw-semibold">{{ formatPrice(getAnnualLimit(wallet.tier)) }}</td>
                        </tr>
                      </table>

                      <div class="d-grid gap-2">
                        <button class="btn btn-primary">
                          <i class="mdi mdi-pencil"></i> Modify Limits
                        </button>
                        <button class="btn btn-outline-secondary">
                          <i class="mdi mdi-upgrade"></i> Upgrade Tier
                        </button>
                      </div>
                    </div>
                  </div>
                </div>

                <div class="col-md-6">
                  <div class="card border">
                    <div class="card-header">
                      <h6 class="mb-0">
                        <i class="mdi mdi-shield-check text-success me-2"></i>
                        Security Features
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span>Two-Factor Authentication</span>
                        <span class="badge bg-success">ENABLED</span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span>SMS Notifications</span>
                        <span class="badge bg-success">ENABLED</span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span>Email Alerts</span>
                        <span class="badge bg-success">ENABLED</span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span>Biometric Login</span>
                        <span class="badge bg-warning">PENDING</span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span>PIN Protection</span>
                        <span class="badge bg-success">ACTIVE</span>
                      </div>
                      <div class="d-flex justify-content-between align-items-center mb-3">
                        <span>Device Registration</span>
                        <span class="badge bg-info">3 DEVICES</span>
                      </div>

                      <div class="d-grid gap-2">
                        <button class="btn btn-warning">
                          <i class="mdi mdi-shield-check"></i> Security Settings
                        </button>
                        <button class="btn btn-outline-danger">
                          <i class="mdi mdi-lock-reset"></i> Reset PIN
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Additional Security Information -->
              <div class="row mt-3">
                <div class="col-12">
                  <div class="card border">
                    <div class="card-header">
                      <h6 class="mb-0">
                        <i class="mdi mdi-history text-info me-2"></i>
                        Recent Security Events
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="timeline">
                        <div class="timeline-item mb-3">
                          <div class="d-flex">
                            <div class="flex-shrink-0">
                              <i class="mdi mdi-login text-success"></i>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-1">Successful Login</h6>
                              <p class="text-muted mb-0">Mobile App • 2 hours ago</p>
                            </div>
                          </div>
                        </div>
                        <div class="timeline-item mb-3">
                          <div class="d-flex">
                            <div class="flex-shrink-0">
                              <i class="mdi mdi-cellphone text-info"></i>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-1">New Device Registered</h6>
                              <p class="text-muted mb-0">iPhone 13 Pro • Yesterday</p>
                            </div>
                          </div>
                        </div>
                        <div class="timeline-item">
                          <div class="d-flex">
                            <div class="flex-shrink-0">
                              <i class="mdi mdi-key text-warning"></i>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-1">PIN Changed</h6>
                              <p class="text-muted mb-0">Web Portal • 3 days ago</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Account History Tab -->
            <div class="tab-pane fade" id="history" role="tabpanel">
              <div class="row mt-3">
                <div class="col-12">
                  <div class="card border">
                    <div class="card-header">
                      <h6 class="mb-0">
                        <i class="mdi mdi-timeline text-primary me-2"></i>
                        Account Timeline
                      </h6>
                    </div>
                    <div class="card-body">
                      <div class="timeline">
                        <div class="timeline-item mb-4">
                          <div class="d-flex">
                            <div class="flex-shrink-0">
                              <div class="avatar-xs">
                                <span class="avatar-title rounded-circle bg-success">
                                  <i class="mdi mdi-check"></i>
                                </span>
                              </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-1">Account Verified</h6>
                              <p class="text-muted mb-1">KYC verification completed successfully</p>
                              <small class="text-muted">{{ formatDateTime(wallet.dateCreated) }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="timeline-item mb-4">
                          <div class="d-flex">
                            <div class="flex-shrink-0">
                              <div class="avatar-xs">
                                <span class="avatar-title rounded-circle bg-primary">
                                  <i class="mdi mdi-wallet"></i>
                                </span>
                              </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-1">Wallet Created</h6>
                              <p class="text-muted mb-1">{{ wallet.type }} wallet account opened</p>
                              <small class="text-muted">{{ formatDateTime(wallet.dateCreated) }}</small>
                            </div>
                          </div>
                        </div>

                        <div class="timeline-item mb-4">
                          <div class="d-flex">
                            <div class="flex-shrink-0">
                              <div class="avatar-xs">
                                <span class="avatar-title rounded-circle bg-info">
                                  <i class="mdi mdi-credit-card"></i>
                                </span>
                              </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-1">First Card Issued</h6>
                              <p class="text-muted mb-1">Virtual debit card activated</p>
                              <small class="text-muted">2 days after account creation</small>
                            </div>
                          </div>
                        </div>

                        <div class="timeline-item">
                          <div class="d-flex">
                            <div class="flex-shrink-0">
                              <div class="avatar-xs">
                                <span class="avatar-title rounded-circle bg-warning">
                                  <i class="mdi mdi-upgrade"></i>
                                </span>
                              </div>
                            </div>
                            <div class="flex-grow-1 ms-3">
                              <h6 class="mb-1">Tier Upgraded</h6>
                              <p class="text-muted mb-1">Account upgraded to {{ wallet.tier }}</p>
                              <small class="text-muted">1 week after account creation</small>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="modal-footer">
          <div class="d-flex justify-content-between w-100">
            <div class="btn-group">
              <button @click="$emit('transfer-funds', wallet)" class="btn btn-primary">
                <i class="mdi mdi-bank-transfer"></i> Transfer Funds
              </button>
              <button class="btn btn-success">
                <i class="mdi mdi-cash-plus"></i> Add Funds
              </button>
              <button class="btn btn-warning">
                <i class="mdi mdi-lock"></i> Freeze Account
              </button>
            </div>
            <div class="btn-group">
              <button class="btn btn-info">
                <i class="mdi mdi-file-document"></i> Generate Statement
              </button>
              <button class="btn btn-secondary">
                <i class="mdi mdi-printer"></i> Print Details
              </button>
              <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  name: "WalletDetailModal",
  props: {
    wallet: {
      type: Object,
      default: () => ({})
    },
    transactions: {
      type: Array,
      default: () => []
    },
    cards: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    formatPrice(value) {
      return new Intl.NumberFormat('en-KE', {
        style: 'currency',
        currency: 'KES',
        minimumFractionDigits: 2
      }).format(value);
    },

    formatDateTime(dateString) {
      return new Date(dateString).toLocaleString('en-KE');
    },

    getWalletTypeBadge(type) {
      const badges = {
        'PERSONAL': 'bg-primary',
        'BUSINESS': 'bg-success',
        'GOVERNMENT': 'bg-info',
        'MERCHANT': 'bg-warning'
      };
      return badges[type] || 'bg-secondary';
    },

    getStatusBadge(status) {
      const badges = {
        'ACTIVE': 'bg-success',
        'SUSPENDED': 'bg-warning',
        'FROZEN': 'bg-danger',
        'CLOSED': 'bg-secondary'
      };
      return badges[status] || 'bg-secondary';
    },

    getDailyLimit(tier) {
      const limits = {
        'TIER_1': 10000,
        'TIER_2': 50000,
        'TIER_3': 100000,
        'BUSINESS_TIER_1': 500000,
        'BUSINESS_TIER_2': 1000000,
        'MERCHANT_PREMIUM': 2000000,
        'GOVERNMENT': 10000000
      };
      return limits[tier] || 10000;
    },

    getMonthlyLimit(tier) {
      return this.getDailyLimit(tier) * 30;
    },

    getSingleTransactionLimit(tier) {
      return this.getDailyLimit(tier) * 0.5;
    },

    getAnnualLimit(tier) {
      return this.getMonthlyLimit(tier) * 12;
    },

    getTodayUsage() {
      // Simulate today's usage calculation
      return Math.random() * this.getDailyLimit(this.wallet.tier) * 0.7;
    },

    getDailyUsagePercent() {
      const usage = this.getTodayUsage();
      const limit = this.getDailyLimit(this.wallet.tier);
      return Math.round((usage / limit) * 100);
    }
  }
}
</script>

<style scoped>
.table td {
  padding: 0.5rem;
  vertical-align: middle;
}

.card-title {
  font-size: 1rem;
  margin-bottom: 0.5rem;
}

.nav-tabs .nav-link {
  color: #495057;
  border: 1px solid transparent;
  border-bottom-color: #dee2e6;
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  background-color: #fff;
  border-color: #0d6efd #0d6efd #fff;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
  isolation: isolate;
}

.progress {
  height: 8px;
  border-radius: 4px;
}

.badge {
  font-size: 0.75rem;
  padding: 0.375em 0.5em;
}

.card:hover {
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
  transition: box-shadow 0.3s ease;
}

.card-hover:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(0,0,0,0.15);
  transition: all 0.3s ease;
}

.timeline {
  position: relative;
}

.timeline-item {
  position: relative;
}

.timeline-item:not(:last-child)::before {
  content: '';
  position: absolute;
  left: 9px;
  top: 30px;
  bottom: -15px;
  width: 1px;
  background-color: #dee2e6;
}

.avatar-xs {
  height: 1.5rem;
  width: 1.5rem;
}

.avatar-title {
  align-items: center;
  display: flex;
  height: 100%;
  justify-content: center;
  width: 100%;
  font-size: 0.75rem;
}

.btn-group .btn {
  margin-right: 0.25rem;
}

.btn-group .btn:last-child {
  margin-right: 0;
}

.font-size-48 {
  font-size: 48px !important;
}

.dropdown-menu {
  border: 1px solid rgba(0,0,0,.15);
  border-radius: 0.375rem;
  box-shadow: 0 0.5rem 1rem rgba(0,0,0,.15);
}

.dropdown-item {
  padding: 0.5rem 1rem;
}

.dropdown-item:hover {
  background-color: #f8f9fa;
}

.modal-xl {
  max-width: 1200px;
}
</style>